/**
 * MatterAssistantPane - a live research session, inside the matter.
 *
 * The research chat is NOT rebuilt here. This pane composes the same typed
 * core the full Research page composes:
 *
 *   useResearchSession → turns, history paging, blob hydration, send/stop/
 *                        rerun/refine/rate, and `ensureChat(seed, matterUuid)`
 *     └ useResearchRun  → the live pubsub run
 *   ResearchTranscript  → the turn list (in `compact` mode)
 *   ResearchComposer    → the input (in `compact` mode)
 *
 * Three things are specific to living in a pane:
 *
 * 1. ATTACH ON FIRST USE. The pane mounts idle. The first send passes
 *    `{ matterUuid }`, so `ensureChat` creates the chat group WITH
 *    `matter_uuid` - that link is what loads the matter's documents and
 *    extracted context into the grounding set for every query, server-side.
 *    Nothing is primed or re-uploaded from here. Chat creation is deduped
 *    inside the hook through one in-flight promise, so an attachment upload
 *    racing a send cannot make two groups.
 *
 * 2. THE CHAT ID IS LOCAL STATE, NOT THE ROUTE. `useResearchSession` normally
 *    takes its uuid from `/research/:chatGroupId`. Here it is given none and
 *    driven by `selectedUuid` below, because the matter page's URL belongs to
 *    the matter: writing a chat id into it would hijack the research route,
 *    and two matters open in two tabs each need their own conversation.
 *
 * 3. THE MATTER'S JURISDICTION AND LANGUAGE, ON THE SESSIONS IT STARTS. A
 *    session created here is scoped to the matter's jurisdiction and answers
 *    in the matter's language, rather than falling back to the platform
 *    defaults - see `applyScope` below for why that is written on the FIRST
 *    send only, and never onto a session opened from the switcher.
 *
 * 4. NO DOCUMENT PANE, AND NO DOCUMENT PRODUCTION. At 360–900px there is
 *    nowhere to put an artifact or a source viewer, so `onOpenArtifact` is not
 *    passed (document-shaped answers then render inline) and a citation with a
 *    real URL opens in a tab.
 *
 *    This pane is deliberately TEXT INTERACTION ONLY. The composer's answer-
 *    shape chip is not rendered and the intent is pinned to `qa`, so the
 *    assistant answers questions about the matter and does not draft, produce a
 *    memo, or generate a document here. Drafting is a first-class surface with
 *    its own editor, versions and export - offering a lesser version of it in a
 *    360px rail produced documents nobody could then work on. The header's
 *    "Open in Research" is the way to the full surface - same chat, every
 *    panel, every intent.
 *
 * 5. AN UPLOAD IS A QUESTION, NOT A FILING. A file dropped on the composer is
 *    attached to the CONVERSATION; it does not become part of the matter unless
 *    someone says so. So after the upload lands, the pane asks - once per batch
 *    - whether it belongs in Case files, and only an explicit yes routes it
 *    through `addFileToMatter`, the same endpoint the Documents tab's uploader
 *    calls. See `offerToFile` below.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  PiArrowSquareOutLight,
  PiChatsCircleLight,
  PiFolderPlusLight,
  PiSparkleLight,
} from 'react-icons/pi';
import { RootState, useAppDispatch } from '../../../store';
import { researchUploadAttachment } from '../../../store/actions/ChatAction';
import { addFileToMatter, streamMatterSource } from '../../../store/actions/Matters/MatterActions';
import { showSidePanel } from '../../../store/actions/sidePanelActions';
import { isExternalUrl } from '../../Research/citationMarkers';
import { contentTypeForPath } from '../../Research/sourceDocument';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import { hideModal, showModal } from '../../../store/actions/modalActions';
import useResearchSession from '../../Research/surface/useResearchSession';
import ResearchTranscript from '../../Research/surface/ResearchTranscript';
import ResearchComposer from '../../Research/surface/ResearchComposer';
import {
  ComposerAttachment,
  attachmentBlobFrom,
  attachmentUuidFrom,
  newAttachmentId,
  readyAttachmentUuids,
  sentAttachments,
} from '../../Research/attachments';
import { isStreaming } from '../../Research/turns';
import { EMPTY_CATALOGUE, loadSourceCatalogue } from '../../Research/sourceCatalogue';
import { ResearchSource } from '../../../store/actions/ChatAction';
import MatterSessionSwitcher from './MatterSessionSwitcher';
import { ConnectedWork, EMPTY_CONNECTED } from './useConnectedWork';
import {
  matterJurisdictionCode,
  matterOutputLanguage,
  sourcesForJurisdiction,
} from './matterResearchScope';

interface Props {
  /** The matter the conversation is grounded in. */
  matterUuid: string;
  /**
   * The matter record, for its jurisdiction and language.
   *
   * The same object (and the same two fields) the Context pane's Overview
   * chips read - a session started here inherits what the matter says rather
   * than the platform defaults.
   */
  matterData?: any;
  /**
   * The matter's connected work, fetched by the page (the rail badge needs
   * the counts whether or not this pane is open). The switcher reads
   * `research_sessions` from it - this pane does not fetch.
   */
  work?: ConnectedWork;
  loading?: boolean;
  /**
   * Re-read the connected work.
   *
   * Called after a send creates a session, so the new conversation appears in
   * the switcher (and in the Connected pane) without a page reload.
   */
  onRefreshConnected?: () => void;
  /**
   * Re-read the matter after a chat upload was filed into Case files.
   *
   * Optional: without it the file still lands, and the Documents tab shows it
   * on its next fetch - this only saves the user a manual refresh.
   */
  onFiledToCaseFiles?: () => void;
}

/**
 * The assistant answers; it does not produce documents.
 *
 * Pinned rather than left to the engine's classifier: a question phrased like
 * "draft me a clause for X" would otherwise be answered with an artifact, in a
 * pane that has no artifact viewer.
 */
const ASSISTANT_INTENT = 'qa' as const;

const MatterAssistantPane: React.FC<Props> = ({
  matterUuid,
  matterData,
  work = EMPTY_CONNECTED,
  loading = false,
  onRefreshConnected,
  onFiledToCaseFiles,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const routeHistory = useHistory();

  const userInfo = useSelector((s: RootState) => (s as any).user?.userInfo);
  // No route uuid: this pane's conversation is not addressed by the URL.
  const session = useResearchSession(
    undefined,
    userInfo?.UserID ?? userInfo?.userId ?? null
  );

  /**
   * The chat the pane is showing. '' is the idle state - no chat yet, and the
   * next message will create one.
   */
  const [selectedUuid, setSelectedUuid] = useState('');
  const [attachments, setAttachments] = useState<ComposerAttachment[]>([]);

  /* ── the matter's scope ─────────────────────────────────────── */

  const jurisdiction = matterJurisdictionCode(matterData);
  const outputLanguage = matterOutputLanguage(matterData);

  /**
   * The catalogue, only so the jurisdiction can be expanded into the index
   * names a source scope is made of.
   *
   * `loadSourceCatalogue` is a module-level cached promise shared with the
   * research page, so on a page where Research has already been opened this
   * costs no request at all, and where it has not, it costs one - the same
   * one the research page would have made.
   */
  const [catalogue, setCatalogue] = useState<ResearchSource[]>([]);
  useEffect(() => {
    // Nothing to expand: a matter with no jurisdiction writes no source scope.
    if (!jurisdiction) return;
    let cancelled = false;
    loadSourceCatalogue(dispatch)
      .catch(() => EMPTY_CATALOGUE)
      .then((loaded) => {
        if (!cancelled) setCatalogue(loaded.jurisdiction_sources || []);
      });
    return () => { cancelled = true; };
  }, [dispatch, jurisdiction]);

  const matterSources = useMemo(
    () => sourcesForJurisdiction(catalogue, jurisdiction),
    [catalogue, jurisdiction]
  );

  /* Open whatever is selected. `open()` is idempotent per uuid, so this can
     never reload a transcript out from under a running query - the same
     contract the full surface relies on for its route effect. */
  useEffect(() => {
    if (selectedUuid) session.open(selectedUuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUuid]);

  /* The mirror of the full surface's route `replace`: a chat created by the
     first send has to become the selection, or the switcher would still read
     as idle while a conversation is on screen. */
  useEffect(() => {
    if (session.chatUuid && session.chatUuid !== selectedUuid) {
      setSelectedUuid(session.chatUuid);
    }
  }, [session.chatUuid, selectedUuid]);

  /* ── attachments ────────────────────────────────────────────── */

  /**
   * Uploading before the first message still has to create the chat WITH the
   * matter - otherwise attaching a file first would silently produce an
   * unlinked session, and every answer in it would miss the matter's context.
   */
  /**
   * "Add this to Case files?" - asked once per upload batch, answered
   * explicitly.
   *
   * The chat attachment and the matter's corpus are two different places, and
   * only one of them is permanent. Filing silently would put whatever someone
   * dropped into a query - a counterparty's draft, a colleague's markup - into
   * the matter's evidence; NOT asking at all was the state before this, and
   * meant a document uploaded "to the matter" was invisible in Documents.
   *
   * Declining costs nothing: the file is already attached to this conversation
   * and every answer can still read it.
   */
  const offerToFile = useCallback((picked: File[]) => {
    if (!picked.length || !matterUuid) return;

    const names = picked.map((file) => file.name);
    const summary = names.length === 1
      ? names[0]
      : t('{{count}} files', { count: names.length });

    dispatch(showModal({
      showModal: true,
      type: 'confirmation',
      title: t('Add to Case files?'),
      message: t('Keep {{name}} with this matter?', { name: summary }),
      subText: t('It joins Documents → Case files and stays part of the matter’s context. Decline and it is only attached to this conversation.'),
      icon: <PiFolderPlusLight className="w-6 h-6 text-champagne" />,
      variant: 'info',
      confirmLabel: t('Add to Case files'),
      cancelLabel: t('Just this chat'),
      onConfirm: async () => {
        dispatch(hideModal());
        try {
          // Same batched uploader the Documents tab uses; it owns the toast,
          // including the one that names files it could not take.
          await dispatch(addFileToMatter(matterUuid, picked));
          onFiledToCaseFiles?.();
        } catch {
          dispatch(showFeedbackModal({
            showModal: true,
            modalType: 'error',
            message: t('Could not add these to Case files.'),
            subMsg: t('They are still attached to this conversation.'),
            duration: 4000,
          }));
        }
      },
      onClose: () => dispatch(hideModal()),
      onCancel: () => dispatch(hideModal()),
    } as any));
  }, [dispatch, matterUuid, onFiledToCaseFiles, t]);

  const handleAttachFiles = useCallback(async (picked: File[]) => {
    if (!picked.length) return;
    const staged: ComposerAttachment[] = picked.map((file) => ({
      id: newAttachmentId(),
      name: file.name,
      size: file.size,
      status: 'uploading',
    }));
    setAttachments((prev) => [...prev, ...staged]);

    const fail = (id: string, error: string) =>
      setAttachments((prev) =>
        prev.map((a) => (a.id === id ? { ...a, status: 'failed', error } : a))
      );

    let uuid: string;
    try {
      uuid = await session.ensureChat(picked[0]?.name, matterUuid);
    } catch {
      staged.forEach((a) => fail(a.id, t('Could not start a session for these files.')));
      return;
    }

    // The endpoint takes ONE file per request.
    const landed = await Promise.all(picked.map(async (file, i) => {
      try {
        const response: any = await dispatch(researchUploadAttachment(uuid, [file]));
        const attachmentUuid = attachmentUuidFrom(response);
        if (!attachmentUuid) throw new Error('missing uuid');
        const stored = attachmentBlobFrom(response);
        setAttachments((prev) =>
          prev.map((a) => (
            a.id === staged[i].id
              ? { ...a, status: 'ready', uuid: attachmentUuid, ...stored }
              : a
          ))
        );
        return file;
      } catch {
        fail(staged[i].id, t('Upload failed. Remove and try again.'));
        return null;
      }
    }));

    // Only what actually attached is offered - a failed upload has nothing to
    // file, and asking about it would be a second thing gone wrong.
    offerToFile(landed.filter(Boolean) as File[]);
  }, [dispatch, session, matterUuid, offerToFile, t]);

  /* ── sending ────────────────────────────────────────────────── */

  /**
   * THE MATTER'S SCOPE, ON A SESSION THIS PANE IS ABOUT TO CREATE.
   *
   * Written as `SendOptions` - the same three fields, in the same shapes, the
   * full Research page passes: `jurisdiction` (a code), `sources` (catalogue
   * rows, which `send` narrows through `toSourceSelection`) and
   * `outputLanguage`. `send` persists all three to the chat's stored settings
   * BEFORE the query is queued, because the engine reads scope and language
   * from the stored rows rather than from the query body.
   *
   * NEW SESSIONS ONLY, and that gating is the point of this function.
   *
   *   • A session opened from the switcher has its own stored jurisdiction,
   *     language and sources - possibly set deliberately on the full research
   *     page. Re-asserting the matter's values on every follow-up would
   *     silently overwrite them, and the user would have no control here to
   *     put them back: this pane has no settings panel.
   *   • On a follow-up the patch is empty, so `send` skips the settings write
   *     entirely and the session keeps whatever it was created with.
   *
   * Absent facts are OMITTED, never sent as null: the settings endpoint reads
   * an explicit null as "clear this override", so a matter with no
   * jurisdiction would actively erase a default rather than defer to it.
   */
  const applyScope = useCallback((isNewSession: boolean) => {
    if (!isNewSession) return {};
    return {
      ...(jurisdiction ? { jurisdiction } : {}),
      // An empty array is not a scope - `send` treats it as "keep what the
      // chat has", which is the right outcome when the catalogue has no
      // index for this jurisdiction.
      ...(matterSources.length ? { sources: matterSources } : {}),
      ...(outputLanguage ? { outputLanguage } : {}),
    };
  }, [jurisdiction, matterSources, outputLanguage]);

  const handleSend = useCallback(async (text: string) => {
    const hadChat = !!session.chatUuid;
    const attachmentUuids = readyAttachmentUuids(attachments);

    const sent = await session.send(text, {
      // Pinned - see ASSISTANT_INTENT. This pane answers; it does not draft.
      intent: ASSISTANT_INTENT,
      ...(attachmentUuids.length ? { attachmentUuids } : {}),
      // The chips must survive the send: the composer clears, and the message
      // row may not echo the files back.
      ...(attachmentUuids.length ? { attachments: sentAttachments(attachments) } : {}),
      // THE ATTACH. Only meaningful on a chat that does not exist yet; the
      // hook drops it on a chat that already carries the link.
      matterUuid,
      // The matter's jurisdiction + language, on the session this send
      // creates. Nothing on a session that already exists.
      ...applyScope(!hadChat),
    });

    // A blocked or failed submit keeps the staged files exactly where they
    // were - discarding them would be work destroyed for nothing.
    if (!sent) return;
    if (attachmentUuids.length) setAttachments([]);
    // A session was just created against this matter; the switcher and the
    // Connected pane are both listing a set that no longer includes it.
    if (!hadChat) onRefreshConnected?.();
  }, [session, attachments, matterUuid, applyScope, onRefreshConnected]);

  /* ── switching ──────────────────────────────────────────────── */

  const selectSession = useCallback((chatUuid: string) => {
    if (!chatUuid || chatUuid === session.chatUuid) return;
    setAttachments([]);
    setSelectedUuid(chatUuid);
  }, [session.chatUuid]);

  /**
   * Back to idle. Nothing is created here - the next message is what makes
   * the session, and makes it against this matter.
   */
  const newSession = useCallback(() => {
    setAttachments([]);
    setSelectedUuid('');
    session.reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.reset]);

  /**
   * A citation with a real, public address: open it.
   *
   * `isExternalUrl` and nothing looser. A datasource citation carries
   * `url: ""` and a tenant blob path, and treating that path as an address
   * opened the app's own origin - the localhost link. Those go through
   * `openCitationRequest` below instead, which fetches the bytes.
   */
  const openCitation = useCallback((url: string) => {
    if (!isExternalUrl(url)) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  /**
   * A citation with no address but a stored file - every datasource source.
   *
   * The bytes come from `POST /matters/stream-source` (the matter's door to
   * the same streamer the research surface uses), and land in the global
   * reader side panel, which is where this matter opens documents everywhere
   * else. The cited quote rides along so the reader can find and scroll to
   * the passage rather than dropping the reader at the top of the file.
   */
  const openCitationRequest = useCallback(async (request: any) => {
    const cited = request?.citation;
    if (!cited) return;
    const blobPath = String(cited.blob_path || '').trim();
    if (!blobPath) {
      openCitation(String(cited.url || ''));
      return;
    }

    const fileName = String(cited.file_name || cited.title || 'Source');
    const contentType = contentTypeForPath(blobPath, cited.content_type);
    const quote = String(cited.locator?.quote || cited.quote || '').trim();

    const reader = (extra: Record<string, any>, loading: boolean) =>
      dispatch(showSidePanel({
        type: 'reader',
        matterId: matterUuid,
        // A datasource citation IS a stored file, so it reads the way the
        // matter's own uploaded documents do.
        sourceType: 'document',
        citation: {
          ...cited,
          title: cited.title || fileName,
          name: fileName,
          contentType,
          // The reader's own jump-to-passage input.
          ...(quote ? { quote, relevant_sections: [quote] } : {}),
          ...extra,
        },
        showSidePanel: true,
        loading,
        message: fileName,
        size: 'xl',
      }));

    reader({}, true);
    try {
      const result: any = await dispatch(streamMatterSource(
        blobPath, 'inline', contentType,
        String(cited.container || '').trim() || undefined,
      ));
      reader(
        result?.success && result.blob
          ? { blob: result.blob, blobObject: result.blob, encrypted: false,
              contentType: result.contentType || contentType }
          : {},
        false
      );
    } catch {
      reader({}, false);
    }
  }, [dispatch, matterUuid, openCitation]);

  const running = session.turns.some(isStreaming);
  const isEmpty = session.turns.length === 0 && !session.isLoadingHistory;

  return (
    <div
      data-testid="matter-assistant-pane"
      data-matter={matterUuid}
      data-chat={session.chatUuid || undefined}
      className="h-full min-h-0 flex flex-col"
    >
      {/* Which session, and the way out to the full surface. */}
      <div className="flex-shrink-0 flex items-center justify-between gap-2
                      px-2 py-1.5 border-b border-hairline-soft">
        <MatterSessionSwitcher
          sessions={work.research_sessions}
          loading={loading}
          activeChatUuid={session.chatUuid}
          activeTitle={session.title}
          onSelect={selectSession}
          onNewSession={newSession}
        />

        {session.chatUuid && (
          <button
            type="button"
            onClick={() => routeHistory.push(`/research/${session.chatUuid}`)}
            data-testid="matter-assistant-open-full"
            aria-label={t('Open in research')}
            title={t('Open in research')}
            className="flex-shrink-0 p-1 rounded-md text-fg-muted hover:text-fg-strong
                       transition-colors duration-200 ease-slide"
          >
            <PiArrowSquareOutLight className="w-4 h-4" />
          </button>
        )}
      </div>

      {isEmpty ? (
        <div
          data-testid="matter-assistant-empty"
          className="flex-1 min-h-0 flex flex-col items-center justify-center
                     gap-3 px-6 py-8 text-center"
        >
          <span className="h-12 w-12 rounded-full bg-surface-sunken border border-hairline-soft
                           flex items-center justify-center">
            <PiChatsCircleLight className="h-6 w-6 text-champagne" aria-hidden />
          </span>
          <div className="space-y-1">
            <p className="text-sm font-medium text-fg-body">{t('Ask about this matter')}</p>
            <p className="text-xs text-fg-muted leading-relaxed max-w-[17rem]">
              {t('It starts a research session with all of this matter’s context loaded.')}
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[10px] text-fg-muted">
            <PiSparkleLight className="h-3 w-3" aria-hidden />
            {t('Answers are grounded in this matter’s documents')}
          </span>
        </div>
      ) : (
        <ResearchTranscript
          compact
          turns={session.turns}
          isLoadingHistory={session.isLoadingHistory}
          isLoadingOlder={session.isLoadingOlder}
          hasMoreHistory={session.hasMoreHistory}
          onLoadOlder={session.loadOlder}
          onHydrate={session.hydrate}
          onOpenCitation={openCitation}
          onOpenCitationRequest={openCitationRequest}
          onFollowUp={handleSend}
          onRerun={session.rerun}
          onSelectVersion={session.selectVersion}
          onRate={session.rate}
          onRefine={session.refine}
        />
      )}

      <ResearchComposer
        compact
        onSend={handleSend}
        onStop={session.stop}
        isRunning={running}
        /* No `onIntentChange` → no answer-shape chip. The intent is fixed. */
        attachments={attachments}
        onAttachFiles={handleAttachFiles}
        onRemoveAttachment={(id) =>
          setAttachments((prev) => prev.filter((a) => a.id !== id))}
        placeholder={t('Ask about this matter…') || ''}
      />
    </div>
  );
};

export default MatterAssistantPane;
